import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { canAcknowledgeRestDay, type RestDayAuthorisationDto } from '@hrms/shared';
import { get, post, toApiError } from '@/lib/api-client';
import { useAuth } from '@/features/auth/use-auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const hours = (minutes: number): string => `${(minutes / 60).toFixed(2)} h`;

/**
 * `2026-08-16` → `Sunday`.
 *
 * Read in UTC to match how the date is stored, so the name cannot slide to the
 * neighbouring day for anyone whose device sits west of Manila.
 */
function weekdayOf(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toLocaleDateString('en-PH', {
    weekday: 'long',
    timeZone: 'UTC',
  });
}

function stamp(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Asia/Manila',
  });
}

/**
 * The employee's own rest day work.
 *
 * Until this existed an employee could be put on a Sunday with no way of seeing
 * it: the authorisation screen is gated on `attendance:authorise-rest-day`,
 * which only supervisors hold, so the record naming them was invisible to them.
 *
 * Rendered only when they actually have one. An empty card on every attendance
 * page for all thirty-odd staff is noise, and this appears exactly when it has
 * something to say.
 */
export function MyRestDayWork() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: rows = [] } = useQuery({
    queryKey: ['rest-day-authorisations', 'mine'],
    queryFn: () => get<RestDayAuthorisationDto[]>('/rest-day-authorisations/mine'),
    // An account with no employee link has no rest day work and the API says so
    // with a 409. That is not an error worth retrying or showing.
    retry: false,
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => post(`/rest-day-authorisations/${id}/acknowledge`, {}),
    onSuccess: () => {
      toast.success('Thanks — recorded that you have seen it');
      void queryClient.invalidateQueries({ queryKey: ['rest-day-authorisations'] });
    },
    onError: (failure) => toast.error(toApiError(failure).message),
  });

  /*
    What is still worth a person's attention: anything from today onwards, plus
    anything already past that they never confirmed — the confirmation is the
    record that they were told, so it stays worth chasing after the fact.

    A rest day worked last month and confirmed at the time is history. Leaving
    those on the dashboard would grow the card indefinitely until the one line
    that matters is buried in a year of settled Sundays.
  */
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

  const live = rows
    .filter((row) => !row.revokedAt)
    .filter((row) => row.date >= today || !row.acknowledgedAt)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (live.length === 0) return null;

  const unseen = live.filter((row) => !row.acknowledgedAt).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="size-4" aria-hidden="true" />
          You have been asked to work a rest day
          {unseen > 0 && <Badge variant="warning">{unseen} to confirm</Badge>}
        </CardTitle>
        <CardDescription>
          Work on a day off is paid at a premium. Confirming only records that you have seen this —
          it does not change your pay, and your hours are counted from your punches either way.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <ul className="divide-y">
          {live.map((row) => {
            // The viewer's own id, not the row's: passing the row's would make
            // the ownership arm of the rule always true and quietly stop
            // testing the thing it exists to test.
            const check = canAcknowledgeRestDay(row, user?.employeeId ?? null);

            return (
              <li key={row.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium">
                    {weekdayOf(row.date)}, {row.date}
                    {row.date < today && (
                      // Past and unconfirmed: still worth confirming, but it
                      // should not read as something coming up.
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        already worked
                      </span>
                    )}
                    {row.expectedMinutes !== null && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        about {hours(row.expectedMinutes)}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">{row.reason}</p>
                  <p className="text-xs text-muted-foreground">
                    Asked by {row.issuedByName} · {stamp(row.issuedAt)}
                  </p>
                </div>

                {row.acknowledgedAt ? (
                  <Badge variant="secondary" className="shrink-0">
                    <ShieldCheck className="size-3" aria-hidden="true" />
                    Confirmed {stamp(row.acknowledgedAt)}
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={!check.ok || acknowledge.isPending}
                    onClick={() => acknowledge.mutate(row.id)}
                  >
                    <Check aria-hidden="true" />
                    I have seen this
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
